import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import MultilingualInput from '../components/MultilingualInput';
import MarkdownEditorMultilingual from '../components/MarkdownEditorMultilingual';
import { STUDENT_MESSAGE_SEND_VARIABLES } from '../utils/studentMessageVariables';

const ALL_LANGUAGES = ['en', 'fr', 'es', 'de', 'pt'];
const NEW_TEMPLATE_ID = '__new__';

const SECTION_SX = {
  p: 2.5,
  mb: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
} as const;

type Template = {
  id: string;
  key: string;
  name: string;
  description: string;
  subject: Record<string, string>;
  body: Record<string, string>;
  sortOrder: number;
  isActive: boolean;
  isAutomaticReminder?: boolean;
  updatedAt: string;
};

const emptyMultilingual = (): Record<string, string> =>
  Object.fromEntries(ALL_LANGUAGES.map((l) => [l, '']));

const StudentMessageTemplates: React.FC = () => {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSubject, setEditSubject] = useState<Record<string, string>>(emptyMultilingual());
  const [editBody, setEditBody] = useState<Record<string, string>>(emptyMultilingual());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isCreating = selectedId === NEW_TEMPLATE_ID;

  const resetEditor = () => {
    setEditName('');
    setEditDescription('');
    setEditSubject(emptyMultilingual());
    setEditBody(emptyMultilingual());
    setSuccess('');
  };

  const loadTemplates = async (keepSelectedId?: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`${apiBase}/student-messages/templates`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.error === 'string' ? data.error : data?.message;
        throw new Error(msg || `Impossible de charger les templates (${res.status})`);
      }
      const list: Template[] = data.templates || [];
      setTemplates(list);

      const idToSelect = keepSelectedId || selectedId;
      if (idToSelect === NEW_TEMPLATE_ID) {
        return;
      }
      const selected = list.find((t) => t.id === idToSelect);
      if (selected) {
        selectTemplate(selected);
      } else if (list.length > 0) {
        selectTemplate(list[0]);
      } else {
        setSelectedId('');
        resetEditor();
      }
    } catch (err: any) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectTemplate = (template: Template) => {
    setSelectedId(template.id);
    setEditName(template.name);
    setEditDescription(template.description || '');
    const subject = emptyMultilingual();
    const body = emptyMultilingual();
    ALL_LANGUAGES.forEach((lang) => {
      subject[lang] = template.subject[lang] || '';
      body[lang] = template.body[lang] || '';
    });
    setEditSubject(subject);
    setEditBody(body);
    setSuccess('');
  };

  const startNewTemplate = () => {
    setSelectedId(NEW_TEMPLATE_ID);
    resetEditor();
    setError('');
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: editName,
        description: editDescription,
        subject: editSubject,
        body: editBody,
      };

      const res = await adminFetch(
        isCreating
          ? `${apiBase}/student-messages/templates`
          : `${apiBase}/student-messages/templates/${selectedId}`,
        {
          method: isCreating ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.error === 'string' ? data.error : data?.message;
        throw new Error(msg || `Impossible de sauvegarder (${res.status})`);
      }

      setSuccess(isCreating ? 'Template créé avec succès' : 'Template enregistré avec succès');
      const savedId = String(data.id);
      setSelectedId(savedId);
      await loadTemplates(savedId);
    } catch (err: any) {
      setError(err.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading && templates.length === 0 && !isCreating) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student-messages')} sx={{ mb: 2 }}>
        Retour
      </Button>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" gutterBottom>
            Templates messages apprenant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Créez et modifiez les modèles d&apos;emails utilisés lors de l&apos;envoi de messages aux
            apprenants.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={startNewTemplate}>
          Nouveau template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '280px 1fr' }} gap={3}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <List disablePadding>
              {isCreating && (
                <ListItemButton selected>
                  <ListItemText
                    primary="Nouveau template"
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                </ListItemButton>
              )}
              {templates.map((t) => (
                <ListItemButton
                  key={t.id}
                  selected={selectedId === t.id}
                  onClick={() => selectTemplate(t)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <span>{t.name}</span>
                        {(t.isAutomaticReminder || t.key.startsWith('auto_reminder_')) && (
                          <Chip size="small" label="Relance auto" color="info" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={t.key}
                    primaryTypographyProps={{ fontWeight: selectedId === t.id ? 700 : 400 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>

        {selectedId && (
          <Card>
            <CardContent>
              <Paper variant="outlined" sx={SECTION_SX}>
                <TextField
                  label="Nom"
                  fullWidth
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  sx={{ mb: 2 }}
                  required
                />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </Paper>

              <Paper variant="outlined" sx={SECTION_SX}>
                <MultilingualInput
                  label="Objet"
                  value={editSubject}
                  onChange={setEditSubject}
                  availableLanguages={ALL_LANGUAGES}
                  required
                />
                <MarkdownEditorMultilingual
                  label="Corps du message"
                  value={editBody}
                  onChange={setEditBody}
                  availableLanguages={ALL_LANGUAGES}
                />
                <Typography variant="body2" fontWeight={600} color="text.secondary" mt={1} mb={1}>
                  Variables disponibles
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Les variables atelier sont résolues à l&apos;envoi selon le groupe de chaque
                  apprenant.
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {STUDENT_MESSAGE_SEND_VARIABLES.map((v) => (
                    <Chip key={v.key} label={`${v.label} — ${v.key}`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Paper>

              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={saving || !editName.trim()}
                  onClick={() => void handleSave()}
                >
                  {isCreating ? 'Créer le template' : 'Enregistrer'}
                </Button>
                {isCreating && (
                  <Button
                    disabled={saving}
                    onClick={() => {
                      if (templates.length > 0) {
                        selectTemplate(templates[0]);
                      } else {
                        setSelectedId('');
                        resetEditor();
                      }
                    }}
                  >
                    Annuler
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default StudentMessageTemplates;
